import { DestroyRef, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, pipe } from "rxjs";
import {map, tap, switchMap, catchError} from 'rxjs/operators';
import { HttpClient } from "@angular/common/http";
import { TokenStorage } from "./token.storage";
//import type { AuthUser, LoginResponse } from "./auth.types";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ToastrService } from "ngx-toastr";
import { AuthApi } from "../shared/sdk";
import { AuthUser } from "../shared/sdk/model/authUser";
import {UserRolesEnum} from './user_roles.enum';

@Injectable({providedIn: 'root'})
export class AuthService {
    private readonly userSubject = new BehaviorSubject<AuthUser | null>(null);
    private readonly isInitializedSubject = new BehaviorSubject<boolean>(false);
    readonly isInitialized$ = this.isInitializedSubject.asObservable();
    readonly user$ = this.userSubject.asObservable();

    constructor(private http: HttpClient,
                private destroyRef: DestroyRef,
                private tokenStorage: TokenStorage,
                private authApi : AuthApi,
                private toastr: ToastrService){}

    get isInitialized(): boolean {
        return this.isInitializedSubject.value;
    }

    get accessToken(): string | null {
        return this.tokenStorage.getAccessToken();
    }

    get user(): AuthUser | null {
        return this.userSubject.value;
    }

    isLoggedIn(): boolean {
        return !! this.accessToken;
    }

    completeLoginWithToken(accessToken: string, userFromResponse?: AuthUser | null): Observable<AuthUser> {
        this.tokenStorage.setAccessToken(accessToken);

        if (userFromResponse) {
        this.userSubject.next(userFromResponse);
        return of(userFromResponse);
        }

        // otherwise load /me and populate userSubject
        return this.loadMe();
    }

    login(email: string, password: string): Observable<AuthUser> {
        return this.authApi.authControllerLogin({email: email, password: password})
        .pipe(
            tap((res) => this.tokenStorage.setAccessToken(res.accessToken)),
            // If backend returns user in login response, you can skip /me
            tap((res) => {
                if (res.user) this.userSubject.next(res.user);
            }),
            map((res) => res.user ?? null),
            // If no user in response, fetch /me:
            tap((maybeUser) => {
                if (!maybeUser) {
                    // fire-and-forget load; route guards can await user$ if needed
                    this.loadMe().subscribe();
                }
            }),
            map(() => this.userSubject.value as AuthUser)
        );
    }

    loadMe(): Observable<AuthUser> {
        //let user = {} as AuthUser;
        return this.authApi.authControllerMe()
            .pipe(
                map((s) => s as unknown as AuthUser),
                tap((s) => this.userSubject.next(s)),
                takeUntilDestroyed(this.destroyRef)
        );
    }

    logout(): void {
        this.tokenStorage.clear();
        this.userSubject.next(null);
    }

    hasRole(required: string | string[]): boolean {
        const user = this.user;
        if (!user) return false;
        if (user.roles.includes(UserRolesEnum.SUPER_ADMIN)) return true;

        const requiredRoles = Array.isArray(required) ? required : [required];
        return requiredRoles.some((r) => user.roles.includes(r as any));
    }

     initFromStorage(): void {
        const token = this.tokenStorage.getAccessToken();
        if (!token) {
            this.userSubject.next(null);
            this.isInitializedSubject.next(true);
            return;
        }

        // token exists -> fetch current user
        this.loadMe().subscribe({
            error: (err) => {
                // token invalid/expired -> clean up
                this.toastr.error('Error', err.error.message);
                this.tokenStorage.clear();
                this.userSubject.next(null);
                this.isInitializedSubject.next(true);  // ADD
            },
            next: (res) => {
                //console.log('It was result');
                this.isInitializedSubject.next(true);  // ADD
                //console.log(res);
            }
        });
    }
}
